import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomNotification } from '@cas/components/data-display/custom-notification/CustomNotification';
import { BadgeStatus } from '@cas/components/data-display/badge-dot/BadgeDot';
import styles from './CompletenessNotifications.module.scss';
import { InvestigationAssetTrackCompleteness } from '@customer/store/apis/investigationsApi';

const statusColorMap: Record<InvestigationAssetTrackCompleteness, BadgeStatus> = {
  None: BadgeStatus.Danger,
  Partial: BadgeStatus.Information,
  Full: BadgeStatus.Neutral, // Not used
  OneSecondFailed: BadgeStatus.Danger,
  OneSecondRequested: BadgeStatus.Information,
  OneSecondPartial: BadgeStatus.Information,
  OneSecondFull: BadgeStatus.Healthy,
};

// A single completeness status can have multiple notifications to communicate compound state.
const notificationsMap: Record<
  InvestigationAssetTrackCompleteness,
  InvestigationAssetTrackCompleteness[]
> = {
  None: [InvestigationAssetTrackCompleteness.None],
  Partial: [InvestigationAssetTrackCompleteness.Partial],
  Full: [], // No notifcations to display.
  OneSecondFailed: [InvestigationAssetTrackCompleteness.OneSecondFailed],
  // Show a notification for both the 10 second partial state and the 1 second requested state.
  OneSecondRequested: [
    InvestigationAssetTrackCompleteness.Partial,
    InvestigationAssetTrackCompleteness.OneSecondRequested,
  ],
  OneSecondPartial: [InvestigationAssetTrackCompleteness.OneSecondPartial],
  OneSecondFull: [InvestigationAssetTrackCompleteness.OneSecondFull],
};

interface CompletenessNotificationsProps {
  status: InvestigationAssetTrackCompleteness;
}

export const CompletenessNotifications = ({
  status,
}: CompletenessNotificationsProps): ReactElement | null => {
  const { t } = useTranslation();

  const notifications = notificationsMap[status];

  return notifications.length ? (
    <div className={styles.container}>
      {notifications.map(statusType => {
        return (
          <CustomNotification
            key={statusType}
            badgeStatus={statusColorMap[statusType]}
            title={t(`AssetTrackCompletenessNotification.${statusType}.Title`)}
            message={t(`AssetTrackCompletenessNotification.${statusType}.Message`)}
          />
        );
      })}
    </div>
  ) : null;
};
